import React, { useId, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from './cn'

export interface AccordionItem {
  /** Stable value used for controlled state and the trigger/panel relationship. */
  value: string
  title: React.ReactNode
  content: React.ReactNode
  /** `option` variant: the line icon before the title. */
  icon?: React.ReactNode
  /** `option` variant: the current choice, in grey under the title. */
  detail?: React.ReactNode
}

/**
 * `default` leaves the look to the page (`data-accordion-*` hooks).
 * `option` is the App surface's「點了才展開」choice row (design `.endw .opt`):
 * icon, title with the current value under it, a chevron, hairlines between
 * rows; the open row's panel sits right under it.
 */
export type AccordionVariant = 'default' | 'option'

export interface AccordionProps {
  items: AccordionItem[]
  variant?: AccordionVariant
  /** Uncontrolled initially expanded item. Omit for every item collapsed. */
  defaultValue?: string | null
  /** Controlled expanded item. `null` collapses every item. */
  value?: string | null
  onValueChange?: (value: string | null) => void
  className?: string
}

const OPTION_TRIGGER =
  'flex min-h-12 w-full items-center gap-2.5 border-b border-[color:var(--line)] bg-transparent py-1.5 text-left text-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--focus-ring)]'

/**
 * A single-open accordion with accessible trigger/panel wiring. Page surfaces
 * own the visual treatment of the default variant through the stable
 * `data-accordion-*` hooks, while the interaction and ARIA contract remain
 * shared in ui-kit.
 */
export function Accordion({
  items,
  variant = 'default',
  defaultValue = null,
  value,
  onValueChange,
  className,
}: AccordionProps) {
  const generatedId = useId()
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(defaultValue)
  const openValue = value === undefined ? uncontrolledValue : value
  const option = variant === 'option'

  const changeValue = (nextValue: string | null) => {
    if (value === undefined) setUncontrolledValue(nextValue)
    onValueChange?.(nextValue)
  }

  return (
    <div data-accordion className={cn(option && 'border-t border-[color:var(--line)]', className)}>
      {items.map((item) => {
        const open = openValue === item.value
        const panelId = `accordion-${generatedId}-${item.value}`
        return (
          <div data-accordion-item key={item.value}>
            <button
              type="button"
              data-accordion-trigger
              data-accordion-variant={variant}
              className={option ? OPTION_TRIGGER : undefined}
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => changeValue(open ? null : item.value)}
            >
              {option ? (
                <>
                  {item.icon != null ? (
                    <span
                      aria-hidden="true"
                      className="flex size-4.5 shrink-0 items-center justify-center [&>svg]:size-full"
                    >
                      {item.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">{item.title}</span>
                    {item.detail != null ? (
                      <span className="block truncate text-xs text-muted">{item.detail}</span>
                    ) : null}
                  </span>
                  <ChevronRight
                    aria-hidden="true"
                    className={cn(
                      'size-4 shrink-0 text-muted transition-transform',
                      open && 'rotate-90'
                    )}
                  />
                </>
              ) : (
                <>
                  <span>{item.title}</span>
                  <span data-accordion-indicator aria-hidden="true">
                    ＋
                  </span>
                </>
              )}
            </button>
            <div
              id={panelId}
              data-accordion-panel
              data-open={String(open)}
              aria-hidden={!open}
              inert={!open}
              hidden={option ? !open : undefined}
              className={option ? 'border-b border-[color:var(--line)] py-2' : undefined}
            >
              <div data-accordion-panel-inner>{item.content}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
